import React, { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as flags from 'country-flag-icons/react/3x2'; // eslint-disable-line import/no-extraneous-dependencies

const DEFAULT_ORIGIN_COORDS = [53.55, 9.99];
const DEFAULT_ORIGIN_COUNTRY_CODE = 'DE';

let countryNameFormatter = null;
const resolveCountryName = (code, fallback) => {
  if (!code) return fallback || '';
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
      if (!countryNameFormatter) {
        countryNameFormatter = new Intl.DisplayNames(['en'], { type: 'region' });
      }
      const name = countryNameFormatter.of(code);
      if (name && name !== code) return name;
    }
  } catch {
    //
  }
  return fallback || code;
};

function project(lat, lng, lon0, lat0) {
  const phi = (lat * Math.PI) / 180;
  const lam = ((lng - lon0) * Math.PI) / 180;
  const phi0 = (lat0 * Math.PI) / 180;
  const cosc = Math.sin(phi0) * Math.sin(phi) + Math.cos(phi0) * Math.cos(phi) * Math.cos(lam);
  const visible = cosc >= -0.05;
  const x = Math.cos(phi) * Math.sin(lam);
  const y = Math.cos(phi0) * Math.sin(phi) - Math.sin(phi0) * Math.cos(phi) * Math.cos(lam);
  return { x, y, visible, depth: cosc };
}

const LAND_DOTS = (() => {
  const pts = [];
  const BANDS = [
    [60, 72, [-25, 30], [55, 180]],
    [50, 60, [-10, 60], [80, 145]],
    [40, 50, [-10, 60], [70, 145], [-130, -65]],
    [30, 40, [-10, 50], [55, 140], [-125, -75]],
    [20, 30, [-15, 50], [55, 130], [-115, -80]],
    [10, 20, [-18, 50], [70, 110], [-105, -85]],
    [0, 10, [-10, 45], [95, 130], [-80, -60]],
    [-10, 0, [10, 45], [95, 145], [-80, -50]],
    [-25, -10, [12, 40], [110, 155], [-75, -40]],
    [-40, -25, [15, 35], [115, 155], [-72, -55]],
  ];
  for (let lat = -40; lat <= 72; lat += 5) {
    for (let lng = -130; lng <= 180; lng += 6) {
      const band = BANDS.find(([latMin, latMax]) => lat >= latMin && lat <= latMax);
      if (band) {
        const lngRanges = band.slice(2);
        const inLand = lngRanges.some(([lngMin, lngMax]) => lng >= lngMin && lng <= lngMax);
        if (inLand) pts.push([lat, lng]);
      }
    }
  }
  return pts;
})();

function GlobeSvg({ originCoords, destinationCoords, size, isActive, uid }) {
  const [t, setT] = useState(0);
  const [rot, setRot] = useState(0);
  const rafRef = useRef();
  const startRef = useRef();
  const lon0 = destinationCoords ? (originCoords[1] + destinationCoords[1]) / 2 : originCoords[1];
  const lat0Center = destinationCoords ? (originCoords[0] + destinationCoords[0]) / 2 : originCoords[0];
  const lat0 = lat0Center * 0.6 + 15;

  useEffect(() => {
    if (!isActive) return undefined;
    const animate = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = (ts - startRef.current) / 1000;
      // Coin flies origin → destination over 2.4s, holds 0.4s, repeats subtly.
      const cycle = 2.8;
      const phase = (elapsed % cycle) / cycle;
      const travelEnd = 0.85;
      let progress;
      if (phase < travelEnd) {
        const p = phase / travelEnd;
        progress = p < 0.5 ? 2 * p * p : 1 - (-2 * p + 2) ** 2 / 2;
      } else {
        progress = 1;
      }
      setT(progress);
      setRot(elapsed * 1.2);
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isActive, originCoords, destinationCoords]);

  const VB = 600;
  const R = 240;
  const CX = VB / 2;
  const CY = VB / 2;

  const effectiveLon0 = lon0 + rot;
  const originP = project(originCoords[0], originCoords[1], effectiveLon0, lat0);
  const originScreen = { x: CX + originP.x * R, y: CY - originP.y * R, visible: originP.visible };
  const destP = destinationCoords ? project(destinationCoords[0], destinationCoords[1], effectiveLon0, lat0) : null;
  const destScreen = destP ? { x: CX + destP.x * R, y: CY - destP.y * R, visible: destP.visible } : null;

  const arcPts = [];
  if (destinationCoords) {
    const STEPS = 60;
    for (let i = 0; i <= STEPS; i += 1) {
      const u = i / STEPS;
      const lat = originCoords[0] + (destinationCoords[0] - originCoords[0]) * u;
      const lng = originCoords[1] + (destinationCoords[1] - originCoords[1]) * u;
      const p = project(lat, lng, effectiveLon0, lat0);
      const lift = Math.sin(u * Math.PI) * 0.18;
      arcPts.push({ x: CX + p.x * R, y: CY - p.y * R - lift * R, visible: p.visible });
    }
  }
  const visibleArc = arcPts.filter((p) => p.visible);
  const arcD = visibleArc.length > 1 ? `M ${visibleArc.map((p, i) => (i === 0 ? `${p.x},${p.y}` : `L ${p.x},${p.y}`)).join(' ')}` : '';

  const coinIdx = arcPts.length > 0 ? Math.min(arcPts.length - 1, Math.max(0, Math.floor(t * (arcPts.length - 1)))) : -1;
  const coin = coinIdx >= 0 ? arcPts[coinIdx] : null;
  const coinVisible = !!(coin && coin.visible);

  const landRender = LAND_DOTS.map(([la, ln]) => {
    const p = project(la, ln, effectiveLon0, lat0);
    if (!p.visible) return null;
    return { x: CX + p.x * R, y: CY - p.y * R, depth: p.depth, la, ln };
  }).filter(Boolean);

  const fillId = `sg-fill-${uid}`;
  const rimId = `sg-rim-${uid}`;
  const coinId = `sg-coin-${uid}`;
  const glowId = `sg-glow-${uid}`;
  const clipId = `sg-clip-${uid}`;

  return (
    <svg viewBox={`0 0 ${VB} ${VB}`} width={size} height={size} style={{ display: 'block', maxWidth: '100%', height: 'auto' }} aria-hidden='true'>
      <defs>
        <radialGradient id={fillId} cx='38%' cy='32%' r='80%'>
          <stop offset='0%' stopColor='#163659' />
          <stop offset='55%' stopColor='#0F2540' />
          <stop offset='100%' stopColor='#060D1A' />
        </radialGradient>
        <radialGradient id={rimId} cx='50%' cy='50%' r='50%'>
          <stop offset='92%' stopColor='rgba(141,189,230,0)' />
          <stop offset='98%' stopColor='rgba(141,189,230,0.35)' />
          <stop offset='100%' stopColor='rgba(141,189,230,0)' />
        </radialGradient>
        <radialGradient id={coinId} cx='35%' cy='35%' r='65%'>
          <stop offset='0%' stopColor='#FFFFFF' />
          <stop offset='60%' stopColor='#C9A961' />
          <stop offset='100%' stopColor='#8B7340' />
        </radialGradient>
        <filter id={glowId} x='-50%' y='-50%' width='200%' height='200%'>
          <feGaussianBlur stdDeviation='3' />
        </filter>
        <clipPath id={clipId}>
          <circle cx={CX} cy={CY} r={R} />
        </clipPath>
      </defs>

      <circle cx={CX} cy={CY} r={R + 14} fill={`url(#${rimId})`} opacity='0.6' />
      <circle cx={CX} cy={CY} r={R} fill={`url(#${fillId})`} />

      <g clipPath={`url(#${clipId})`} stroke='rgba(141,189,230,0.08)' strokeWidth='1' fill='none'>
        {[-60, -30, 0, 30, 60].map((lat) => {
          const pts = [];
          for (let lng = -180; lng <= 180; lng += 4) {
            const p = project(lat, lng, effectiveLon0, lat0);
            if (p.visible) pts.push({ x: CX + p.x * R, y: CY - p.y * R });
          }
          if (pts.length < 2) return null;
          const d = `M ${pts.map((p, i) => `${i ? 'L' : ''} ${p.x},${p.y}`).join(' ')}`;
          return <path key={`par${lat}`} d={d} />;
        })}
        {[-150, -120, -90, -60, -30, 0, 30, 60, 90, 120, 150, 180].map((lng) => {
          const pts = [];
          for (let lat = -80; lat <= 80; lat += 4) {
            const p = project(lat, lng, effectiveLon0, lat0);
            if (p.visible) pts.push({ x: CX + p.x * R, y: CY - p.y * R });
          }
          if (pts.length < 2) return null;
          const d = `M ${pts.map((p, i) => `${i ? 'L' : ''} ${p.x},${p.y}`).join(' ')}`;
          return <path key={`mer${lng}`} d={d} />;
        })}
      </g>

      <g clipPath={`url(#${clipId})`}>
        {landRender.map((d) => (
          <circle key={`${d.la},${d.ln}`} cx={d.x} cy={d.y} r={1.6} fill='rgba(141,189,230,0.55)' opacity={0.4 + Math.min(1, d.depth) * 0.6} />
        ))}
      </g>

      {arcD && (
        <g clipPath={`url(#${clipId})`}>
          <path d={arcD} fill='none' stroke='rgba(201,169,97,0.35)' strokeWidth='1.5' strokeDasharray='2 5' strokeLinecap='round' />
          <path
            d={arcD}
            fill='none'
            stroke='rgba(201,169,97,0.95)'
            strokeWidth='1.8'
            strokeDasharray='2 5'
            strokeLinecap='round'
            style={{ strokeDashoffset: -t * 200, filter: `url(#${glowId})` }}
          />
        </g>
      )}

      {originScreen.visible && (
        <g>
          <circle cx={originScreen.x} cy={originScreen.y} r={9} fill='rgba(141,189,230,0.18)'>
            <animate attributeName='r' values='9;16;9' dur='2s' repeatCount='indefinite' />
            <animate attributeName='opacity' values='0.5;0;0.5' dur='2s' repeatCount='indefinite' />
          </circle>
          <circle cx={originScreen.x} cy={originScreen.y} r={4.5} fill='#8DBDE6' />
          <circle cx={originScreen.x} cy={originScreen.y} r={2} fill='#FFFFFF' />
        </g>
      )}

      {destScreen && destScreen.visible && (
        <g>
          <circle cx={destScreen.x} cy={destScreen.y} r={t > 0.95 ? 14 : 8} fill='rgba(201,169,97,0.25)'>
            <animate attributeName='r' values={t > 0.95 ? '8;28;8' : '8;14;8'} dur={t > 0.95 ? '1s' : '2.2s'} repeatCount='indefinite' />
            <animate attributeName='opacity' values='0.6;0;0.6' dur={t > 0.95 ? '1s' : '2.2s'} repeatCount='indefinite' />
          </circle>
          <circle cx={destScreen.x} cy={destScreen.y} r={5} fill='#C9A961' />
          <circle cx={destScreen.x} cy={destScreen.y} r={2} fill='#FFFFFF' />
        </g>
      )}

      {coinVisible && (
        <g>
          <circle cx={coin.x} cy={coin.y} r={12} fill='rgba(201,169,97,0.25)' filter={`url(#${glowId})`} />
          <circle cx={coin.x} cy={coin.y} r={6} fill={`url(#${coinId})`} />
          <circle cx={coin.x} cy={coin.y} r={2} fill='#FFFFFF' opacity='0.9' />
        </g>
      )}
    </svg>
  );
}

GlobeSvg.propTypes = {
  originCoords: PropTypes.arrayOf(PropTypes.number).isRequired,
  destinationCoords: PropTypes.arrayOf(PropTypes.number),
  size: PropTypes.number.isRequired,
  isActive: PropTypes.bool.isRequired,
  uid: PropTypes.string.isRequired,
};

function SendingGlobe({
  destinationCoords,
  destinationCountryCode,
  destinationName,
  originCoords = DEFAULT_ORIGIN_COORDS,
  originCity = 'Hamburg',
  size = 380,
  isActive = true,
  amountDisplay,
  recipientName,
  status = 'In Flight',
  className = '',
  globeOnly = false,
}) {
  const reactId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const destCode = (destinationCountryCode || '').toUpperCase();
  const DestFlag = destCode ? flags[destCode] : null;
  const resolvedDestName = destinationName || resolveCountryName(destCode, '');

  const hasTitle = !!amountDisplay || !!resolvedDestName;
  const hasSubtitle = !!originCity || !!recipientName;

  if (globeOnly) {
    return (
      <div className={`flex justify-center ${className}`}>
        <GlobeSvg originCoords={originCoords} destinationCoords={destinationCoords} size={size} isActive={isActive} uid={reactId} />
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-[#060D1A] px-6 py-8 sm:px-10 sm:py-10 text-white shadow-xl ${className}`}
      style={{
        backgroundImage:
          'radial-gradient(ellipse at 50% 0%, rgba(141,189,230,0.10) 0%, rgba(6,13,26,0) 55%), radial-gradient(ellipse at 50% 100%, rgba(201,169,97,0.06) 0%, rgba(6,13,26,0) 55%)',
      }}
    >
      {(status || hasTitle || hasSubtitle) && (
        <div className='text-center mb-6'>
          {status && <p className='text-[11px] font-semibold tracking-[0.28em] uppercase text-[#8DBDE6]/70 mb-3'>{status}</p>}
          {hasTitle && (
            <h3
              className='font-serif text-3xl sm:text-4xl leading-tight text-white tracking-tight'
              style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", Garamond, Georgia, "Times New Roman", serif' }}
            >
              {amountDisplay && <span>Sending {amountDisplay}</span>}
              {amountDisplay && resolvedDestName && <span> to </span>}
              {!amountDisplay && resolvedDestName && <span>To </span>}
              {resolvedDestName && (
                <span className='whitespace-nowrap'>
                  {DestFlag && (
                    <DestFlag
                      className='inline-block align-[-0.18em] mx-1 rounded-[2px] shadow-[0_0_0_1px_rgba(255,255,255,0.08)]'
                      style={{ width: '1.35em', height: '0.9em' }}
                      title={`${destCode} flag`}
                    />
                  )}{' '}
                  {resolvedDestName}
                </span>
              )}
            </h3>
          )}
          {hasSubtitle && (
            <p className='text-sm text-[#8DBDE6]/70 mt-2'>
              {originCity && <span>From {originCity}</span>}
              {originCity && recipientName && <span className='mx-1.5'>→</span>}
              {recipientName && <span>{recipientName}</span>}
            </p>
          )}
        </div>
      )}
      <div className='flex justify-center'>
        <GlobeSvg originCoords={originCoords} destinationCoords={destinationCoords} size={size} isActive={isActive} uid={reactId} />
      </div>
    </div>
  );
}

SendingGlobe.propTypes = {
  destinationCoords: PropTypes.arrayOf(PropTypes.number),
  destinationCountryCode: PropTypes.string,
  destinationName: PropTypes.string,
  originCoords: PropTypes.arrayOf(PropTypes.number),
  originCity: PropTypes.string,
  size: PropTypes.number,
  isActive: PropTypes.bool,
  amountDisplay: PropTypes.string,
  recipientName: PropTypes.string,
  status: PropTypes.string,
  className: PropTypes.string,
  globeOnly: PropTypes.bool,
};

export default SendingGlobe;
export { DEFAULT_ORIGIN_COORDS, DEFAULT_ORIGIN_COUNTRY_CODE };
